import { Dialog, Transition } from '@headlessui/react';
import { Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { addDays } from 'date-fns';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';

import { queryClient } from '../../App';
import { PermissionMessages } from '../../constants/SGFTUserTypes';
import api from '../../services/apiSgft';
import { actualUserAtom } from '../../state/ActualUser.atom';
import {
	CoverageStructure,
	CoverPerson,
	ModalRequest,
	ModalResponse,
	QualifyingPeriod,
	Scales,
	VacationDataResponse,
	VacationPeriod,
	VacationScales,
	WorkingDays,
} from '../../types/VacationRegister';
import {
	addInclusiveCalendarDays,
	civilDate,
	formatDate,
	removeTimeZone,
} from '../../utils/formatDate';
import {
	userCanSave,
	userIsRhOrAdmin,
} from '../../utils/handleSavePermissions';
import { toFormTransportDataShape } from '../../utils/normalizeVacationCoverTransportData';
import { Button } from '../atoms/Button';
import CloseButton from '../atoms/CloseButton';
import ControlledCalendar from '../atoms/ControlledCalendar';
import ControlledCheckbox from '../atoms/ControlledCheckbox';
import ControlledSimpleSelect from '../atoms/ControlledSimpleSelect';
import SimpleSelect from '../atoms/SimpleSelect';
import Spinner from '../atoms/Spinner';
import SubtitleText from '../atoms/SubtitleText';
import ModalConfirmation from '../molecules/ConfirmationModal';
import ExpandableSubtitle from '../molecules/ExpandableSubtitle';
import type { TransportDay } from '../molecules/TransportSection';
import VacationCoverPlan from '../molecules/VacationCoverPlan';

function completePeriods(
	vacationPeriods: VacationPeriod[],
	vacationScales: VacationScales[],
	vacationTypeId: number,
): (VacationPeriod | null)[] | VacationPeriod[] {
	const scale = vacationScales
		.flatMap((item) => item.scales)
		.find((s) => Number(s.id) === vacationTypeId);
	if (scale) {
		const daysOfPeriods = [
			scale.firstPeriod,
			scale.secondPeriod,
			scale.thirdPeriod,
		].filter((val): val is number => typeof val === 'number');
		const newPeriods: (VacationPeriod | null)[] = [null, null, null];

		daysOfPeriods.forEach((days, index) => {
			const sameDurationSlotIndex = daysOfPeriods
				.slice(0, index)
				.filter((periodDays) => periodDays === days).length;
			const orderedCandidates = vacationPeriods
				.filter((period) => period.totalDays === days)
				.sort((first, second) => {
					const firstStartDate = first.startDate
						? new Date(first.startDate).getTime()
						: 0;
					const secondStartDate = second.startDate
						? new Date(second.startDate).getTime()
						: 0;

					return firstStartDate - secondStartDate;
				});
			const matchedPeriod = orderedCandidates[sameDurationSlotIndex];

			if (matchedPeriod) {
				newPeriods[index] = matchedPeriod;
			} else {
				newPeriods[index] = {
					totalDays: days,
					startDate: null,
					endDate: null,
					salaryAdvance: false,
					vacationCovers: [] as CoverPerson[],
				};
			}
		});
		return newPeriods;
	}
	return vacationPeriods;
}

function normalizeNeedTransport(value: unknown): boolean {
	if (typeof value === 'boolean') return value;
	if (typeof value === 'string') return value === 'true';
	return false;
}

function toComparableDate(value: unknown): string | null {
	if (!value) return null;
	const parsed = value instanceof Date ? value : new Date(value as string);
	if (Number.isNaN(parsed.getTime())) return null;
	return removeTimeZone(parsed).toDateString();
}

type CoverWithTransport = CoverPerson & {
	needTransport?: boolean;
	transportData?: {
		transportDays?: TransportDay[];
		passengerInfo?: { phone?: string };
	};
};

interface Props {
	employeeId: number;
	vacationId: number;
	modalIsOpen: boolean;
	handleClose: () => void;
}

export default function VacationRegisterModal({
	employeeId,
	vacationId,
	handleClose,
	modalIsOpen,
}: Props) {
	const [isSubmiting, setIsSubmiting] = useState(false);
	const [startDatesOutOfPolicy, setStartDatesOutOfPolicy] = useState(false);
	const [hasEvaluatedStartDates, setHasEvaluatedStartDates] = useState(false);
	const actualUser = useRecoilValue(actualUserAtom);
	const canSaveOutOfPolicy = userIsRhOrAdmin();
	const [request, setRequest] = useState<ModalRequest | undefined>(undefined);
	const [periods, setPeriods] = useState<Scales | null>();
	const [calendar, setCalendar] = useState(false);
	const [allowedStartDates, setAllowedStartDates] = useState<
		Record<number, string[]>
	>({});
	const [selectedQualifyingPeriod, setSelectedQualifyingPeriod] = useState<
		number | null
	>(null);
	const [isFetchingVacation, setIsFetchingVacation] = useState(false);
	const [employeeDeallocation, setEmployeeDeallocation] = useState<Array<{
		days: Array<string>;
		coverageTeamName: string;
		coverageEmployeeName: string;
		teamName: string;
		employeeName: string;
	}> | null>(null);
	const isEdit = request?.vacation?.id;
	const minimumDate = addDays(new Date(), 35);

	const methods = useForm<ModalResponse>();

	const fetchVacationData = useCallback(
		async (vacationIdParam: number) => {
			setIsFetchingVacation(true);
			try {
				const response = await api.get<VacationDataResponse>(
					`/vacation/data?vacationId=${vacationIdParam}`,
				);
				const { data } = response;
				if (data) {
					const vacationPeriods = data.vacation?.vacationPeriods ?? [];
					const vacationTypeId = Number(data.vacation?.vacationTypeId);
					const newPeriods = completePeriods(
						[...vacationPeriods],
						data.vacationScales,
						vacationTypeId,
					);
					const vacationPeriodsForForm = newPeriods.map((period) => {
						if (period == null) {
							return {
								totalDays: 0,
								startDate: null,
								endDate: null,
								salaryAdvance: false,
								vacationCovers: [] as CoverPerson[],
								vacationCoversWorkDays: [] as {
									startDate: Date;
									endDate: Date;
								}[],
							};
						}
						return {
							...period,
							startDate: period.startDate
								? civilDate(period.startDate as Date | string)
								: period.startDate,
							endDate: period.endDate
								? civilDate(period.endDate as Date | string)
								: period.endDate,
							vacationCovers: (period.vacationCovers ?? []).map(
								(
									cover: CoverPerson & {
										transportData?: unknown;
										needTransport?: unknown;
									},
								) => ({
									...cover,
									startDate: cover.startDate
										? formatDate(removeTimeZone(new Date(cover.startDate)))
										: cover.startDate,
									endDate: cover.endDate
										? formatDate(removeTimeZone(new Date(cover.endDate)))
										: cover.endDate,
									needTransport: normalizeNeedTransport(cover.needTransport),
									transportData: toFormTransportDataShape(cover.transportData),
								}),
							),
						};
					});
					methods.reset({
						employeeId: data?.id,
						totalVacationDays: data?.vacation?.totalVacationDays,
						outOfPolicy: data?.vacation?.outOfPolicy ?? false,
						vacationScale: data?.vacation?.vacationTypeId,
						vacationPeriods: vacationPeriodsForForm,
					});
					setRequest(data);
				}
			} catch (e) {
				toast.error('Erro ao carregar os dados', {
					theme: 'colored',
					toastId: 'error',
				});
				throw e;
			}
			setIsFetchingVacation(false);
		},
		[methods],
	);

	const fetchQualifyingPeriods = async () => {
		try {
			const response = await api.get<QualifyingPeriod[]>(
				`/vacation/qualifyingPeriods?employeeId=${employeeId}`,
			);
			return response.data.map((qualifyingPeriod) => ({
				label: qualifyingPeriod.name,
				value: qualifyingPeriod.value,
			}));
		} catch (e) {
			toast.error('Erro ao carregar os dados do período aquisitivo', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	const { data: qualifyingPeriods } = useQuery(
		['vacationQualifyingPeriods', employeeId],
		fetchQualifyingPeriods,
		{
			retry: false,
			refetchOnMount: false,
		},
	);

	const fetchAvailableTeamStructures = async () => {
		const response = await api.get<CoverageStructure[]>(
			'/vacation/coverage/availableStructures',
			{
				params: {
					employeeId,
				},
			},
		);

		return response.data;
	};

	const { data: structures } = useQuery(
		['coverageAvailableStructures', employeeId],
		fetchAvailableTeamStructures,
		{
			retry: false,
			refetchOnMount: false,
		},
	);

	const handleSelectQualifyingPeriod = useCallback(
		(vacationIdParam: number) => {
			setRequest(undefined);
			setSelectedQualifyingPeriod(vacationIdParam);
			fetchVacationData(vacationIdParam);
			methods.reset({
				outOfPolicy: request?.vacation?.outOfPolicy ?? false,
			});
			setPeriods(null);
		},
		[request?.vacation?.outOfPolicy, methods, fetchVacationData],
	);

	useEffect(() => {
		if (qualifyingPeriods && vacationId !== 0) {
			const filterQualifying = qualifyingPeriods.filter(
				(d: { value: number }) => d.value === vacationId,
			).length;
			if (filterQualifying !== 0) {
				handleSelectQualifyingPeriod(vacationId);
			}
		}
	}, [qualifyingPeriods, vacationId, handleSelectQualifyingPeriod]);

	const fetchVacationCover = useCallback(
		async (
			startDate: string,
			endDate: string,
		): Promise<{ startDate: Date; endDate: Date }[]> => {
			try {
				const response = await api.get<WorkingDays>(
					`/vacation/workDays?startDate=${startDate}&endDate=${endDate}&employeeId=${employeeId}`,
				);
				return response.data.workDaysPeriods.map((period) => ({
					startDate: removeTimeZone(new Date(period.startDate)),
					endDate: removeTimeZone(new Date(period.endDate)),
				}));
			} catch (e) {
				toast.error('Erro ao carregar os dados', {
					theme: 'colored',
					toastId: 'error',
				});
				throw e;
			}
		},
		[employeeId],
	);

	const totalDaysWatch = methods.watch('totalVacationDays', request?.totalDays);
	const vacationScaleWatch = methods.watch(
		'vacationScale',
		request?.vacationScale,
	);
	const vacationPeriodsWatch = methods.watch(
		'vacationPeriods',
		request?.vacation?.vacationPeriods,
	);
	const outOfPolicyWatch = methods.watch(
		'outOfPolicy',
		request?.vacation ? request?.vacation.outOfPolicy : false,
	);

	const getAllowedDates = useCallback(
		async (
			vacationIdParam: string,
			vacationTypeId: string,
			outOfPolicy: boolean,
		) => {
			try {
				const response = await api.get(`/vacation/allowedVacationDates`, {
					params: {
						vacationId: vacationIdParam,
						vacationTypeId,
						outOfPolicy,
					},
				});

				setAllowedStartDates(response.data.allowedStartDates);
			} catch (e) {
				toast.error('Erro ao carregar os dados', {
					theme: 'colored',
					toastId: 'error',
				});
				throw e;
			}
		},
		[],
	);

	useEffect(() => {
		if (vacationScaleWatch) {
			getAllowedDates(isEdit ?? '', vacationScaleWatch, outOfPolicyWatch);
		}
	}, [vacationScaleWatch, outOfPolicyWatch, isEdit, getAllowedDates]);

	const handleChangeVacationScale = useCallback(async () => {
		if (!request) return;

		const vacationTypeId = request?.vacation?.vacationTypeId;
		if (vacationScaleWatch === undefined && vacationTypeId) {
			methods.setValue('vacationScale', vacationTypeId);
		}

		const selectedScale = request.vacationScales
			.find((el) => el.totalDays === totalDaysWatch)
			?.scales.find((el) => el.id === vacationScaleWatch);

		if (selectedScale) {
			setPeriods(selectedScale);
			const scaleNames = selectedScale.name.split('x');
			const newVacationPeriods = scaleNames.map(async (el, index) => {
				const currentPeriod = methods.getValues(`vacationPeriods.${index}`);
				const days = parseInt(el, 10);
				const start = currentPeriod?.startDate
					? civilDate(currentPeriod.startDate)
					: null;
				const end = start ? addInclusiveCalendarDays(start, days) : null;
				const getVacationCovers = async () => {
					if (start && end) {
						return await fetchVacationCover(
							formatDate(start),
							formatDate(end),
						);
					}
					return currentPeriod?.vacationCoversWorkDays ?? [];
				};
				const vacationCoversWorkDays = await getVacationCovers();
				return {
					totalDays: days,
					startDate: start ?? '',
					endDate: end ?? '',
					salaryAdvance: currentPeriod?.salaryAdvance ?? false,
					vacationCovers: (currentPeriod?.vacationCovers ?? []).map((c) => ({
						...(c as CoverPerson),
						needTransport: normalizeNeedTransport(
							(c as { needTransport?: unknown }).needTransport,
						),
					})),
					vacationCoversWorkDays,
				} as VacationPeriod;
			});

			methods.setValue(
				'vacationPeriods',
				await Promise.all(newVacationPeriods),
			);
			methods.setValue('vacationTypeId', vacationScaleWatch);
		}
	}, [
		request,
		vacationScaleWatch,
		methods,
		totalDaysWatch,
		fetchVacationCover,
	]);

	useEffect(() => {
		handleChangeVacationScale();
	}, [handleChangeVacationScale]);

	function getScales(totalDays: number): { value: string; label: string }[] {
		if (request) {
			const vacationScales = request?.vacationScales.filter(
				(el) => el.totalDays === totalDays,
			);
			const firstBucket = vacationScales[0];
			if (!firstBucket) {
				return [{ value: '0', label: '' }];
			}

			if (request.isADM || request.isDoctor) {
				return firstBucket.scales.map((el) => ({
					value: el.id,
					label: el.name,
				}));
			}

			if (outOfPolicyWatch) {
				return firstBucket.scales.map((el) => ({
					value: el.id,
					label: el.name,
				}));
			}

			return firstBucket.scales
				.filter((el) => el.outOfPolicy === outOfPolicyWatch)
				.map((el) => ({
					value: el.id,
					label: el.name,
				}));
		}

		return [{ value: '0', label: '' }];
	}

	function isSelectedScaleOutOfPolicy() {
		if (!request) {
			return false;
		}

		const vacationScales = request.vacationScales.filter(
			(el) => el.totalDays === totalDaysWatch,
		);
		const firstBucket = vacationScales[0];
		if (!firstBucket) {
			return false;
		}

		const selectedScale = firstBucket.scales.find(
			(scale) => scale.id === vacationScaleWatch,
		);

		return selectedScale?.outOfPolicy === true;
	}

	function checkIfScaleIsOutOfPolicy() {
		return !isSelectedScaleOutOfPolicy();
	}

	function verifyStartDateAllowed(
		dates: {
			totalDays: number;
			startDate: string | null;
		}[],
		allowedDates: Record<string, string[]>,
	): boolean {
		for (const date of dates) {
			if (date.startDate === null) {
				continue;
			}
			const allowedForTotalDays = allowedDates[date.totalDays] ?? [];
			const isAllowed = allowedForTotalDays.some(
				(dateString) => toComparableDate(dateString) === date.startDate,
			);
			if (!isAllowed) {
				return false;
			}
		}
		return true;
	}

	function getStartDates(vacationPeriods: VacationPeriod[]) {
		const startDates = vacationPeriods.map((period: VacationPeriod) => {
			const startDate = {
				totalDays: period.totalDays,
				startDate: toComparableDate(period.startDate),
			};
			return startDate;
		});
		return startDates;
	}

	function checkIfStartDatesAreAllowed(vacationPeriods: VacationPeriod[]) {
		const startDates = getStartDates(vacationPeriods);
		const isStartDateAllowed = verifyStartDateAllowed(
			startDates,
			allowedStartDates,
		);
		return isStartDateAllowed;
	}

	async function fetchInPolicyDates() {
		try {
			const response = await api.get(`/vacation/allowedVacationDates`, {
				params: {
					vacationId: isEdit ?? '',
					vacationTypeId: vacationScaleWatch,
					outOfPolicy: false,
				},
			});
			return response.data.allowedStartDates;
		} catch {
			toast.error('Erro ao salvar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
		}
	}

	async function checkIfStartDateIsOutOfPolicy(
		vacationPeriods: VacationPeriod[],
	) {
		const startDates = getStartDates(vacationPeriods);
		const onPolicyStartDates = await fetchInPolicyDates();
		const isStartDateOutOfPolicy = !verifyStartDateAllowed(
			startDates,
			onPolicyStartDates ?? {},
		);
		return isStartDateOutOfPolicy;
	}

	const scaleOutOfPolicy = isSelectedScaleOutOfPolicy();
	const isEffectivelyOutOfPolicy = scaleOutOfPolicy || startDatesOutOfPolicy;
	const blockedByOutOfPolicy =
		isEffectivelyOutOfPolicy && !canSaveOutOfPolicy;

	const startDatesSignature = JSON.stringify(
		(vacationPeriodsWatch ?? []).map((period) => ({
			totalDays: period?.totalDays ?? null,
			startDate: toComparableDate(period?.startDate),
		})),
	);

	useEffect(() => {
		let cancelled = false;

		const evaluateStartDatesOutOfPolicy = async () => {
			try {
				if (!request || !vacationScaleWatch) {
					if (!cancelled) {
						setStartDatesOutOfPolicy(false);
					}
					return;
				}

				const periods = methods.getValues('vacationPeriods') ?? [];
				const hasAnyStartDate = periods.some((period) => period?.startDate);
				if (!hasAnyStartDate) {
					if (!cancelled) {
						setStartDatesOutOfPolicy(false);
					}
					return;
				}

				const isOutOfPolicy = await checkIfStartDateIsOutOfPolicy(periods);
				if (!cancelled) {
					setStartDatesOutOfPolicy(isOutOfPolicy);
				}
			} catch (error) {
				if (!cancelled) {
					setStartDatesOutOfPolicy(false);
				}
			} finally {
				if (!cancelled) {
					setHasEvaluatedStartDates(true);
				}
			}
		};

		evaluateStartDatesOutOfPolicy();

		return () => {
			cancelled = true;
		};
	}, [startDatesSignature, vacationScaleWatch, isEdit, request, methods]);

	useEffect(() => {
		if (
			canSaveOutOfPolicy ||
			!hasEvaluatedStartDates ||
			!outOfPolicyWatch ||
			scaleOutOfPolicy ||
			startDatesOutOfPolicy
		) {
			return;
		}

		methods.setValue('outOfPolicy', false);
	}, [
		canSaveOutOfPolicy,
		hasEvaluatedStartDates,
		outOfPolicyWatch,
		scaleOutOfPolicy,
		startDatesOutOfPolicy,
		methods,
	]);

	function getTotalDays(): { value: number; label: string }[] {
		return [
			{ value: 30, label: '30d' },
			{ value: 20, label: '20d' },
			{ value: 24, label: '24d' },
			{ value: 18, label: '18d' },
			{ value: 12, label: '12d' },
		];
	}

	const verifyCoverage = async (modalData: ModalResponse) => {
		setIsSubmiting(true);
		const covers = modalData.vacationPeriods.flatMap(
			(period: VacationPeriod) => period.vacationCovers,
		);
		try {
			const response = await api.post('vacation/verify-coverage', {
				vacationCovers: covers,
				employeeId,
			});
			if (response.data.employeeDeallocation.length) {
				setEmployeeDeallocation(response.data.employeeDeallocation);
			} else {
				setEmployeeDeallocation(null);
				await onSubmit();
			}
		} catch {
			toast.error('Erro ao verificar a cobertura', { theme: 'colored' });
		}
		setIsSubmiting(false);
	};

	function handleVacationPeriodSubmissionData(modalData: ModalResponse) {
		const vacationPeriods = modalData.vacationPeriods.map(
			(period: VacationPeriod) => {
				const totalDays = period.totalDays;
				let endDate = period.endDate;

				if (period.startDate) {
					endDate = addInclusiveCalendarDays(period.startDate, totalDays);
				} else {
					period.startDate = null;
					endDate = null;
				}
				const { vacationCovers } = period;
				const normalizedVacationCovers = vacationCovers.map(
					(cover: CoverWithTransport) => {
						const transportData = cover.transportData
							? {
									...cover.transportData,
									transportDays: cover.transportData.transportDays?.map(
										(transportDay: TransportDay) => {
											const {
												requestiaId: _id,
												requestiaStatus: _status,
												...rest
											} = transportDay;
											return rest;
										},
									),
								}
							: undefined;
						return { ...cover, transportData };
					},
				);
				return { ...period, endDate, vacationCovers: normalizedVacationCovers };
			},
		);
		return vacationPeriods;
	}

	const onSubmit = async () => {
		const modalData = methods.getValues();
		setEmployeeDeallocation(null);

		const isStartDateAllowed = checkIfStartDatesAreAllowed(
			modalData.vacationPeriods,
		);
		if (!isStartDateAllowed) {
			toast.error('Data de início invalída', {
				theme: 'colored',
				toastId: 'error',
			});
			return;
		}

		const scaleIsOutOfPolicy = isSelectedScaleOutOfPolicy();
		const shiftOutOfPolicy = checkIfScaleIsOutOfPolicy();
		const startDateOufOfPolicy = await checkIfStartDateIsOutOfPolicy(
			modalData.vacationPeriods,
		);

		if ((scaleIsOutOfPolicy || startDateOufOfPolicy) && !canSaveOutOfPolicy) {
			toast.error(PermissionMessages.OutOfPolicyNoPermission, {
				theme: 'colored',
				toastId: 'error',
			});
			return;
		}

		const normalizedOutOfPolicy =
			scaleIsOutOfPolicy ||
			startDateOufOfPolicy ||
			(outOfPolicyWatch && canSaveOutOfPolicy);

		const vacationPeriods = handleVacationPeriodSubmissionData(modalData);
		const { vacationScale: _vacationScale, ...modalDataWithoutVacationScale } =
			modalData;

		const submissionData = {
			...modalDataWithoutVacationScale,
			outOfPolicy: normalizedOutOfPolicy,
			vacationPeriods,
			shiftOutOfPolicy,
			startDateOufOfPolicy,
		};
		console.log(submissionData);

		modalData.vacationPeriods.forEach((period: VacationPeriod) => {
			if (!period.startDate && period.vacationCovers.length > 0) {
				toast.error('Plano de cobertura sem período de férias', {
					theme: 'colored',
					toastId: 'error',
				});
			}
		});
		const endpoint = isEdit
			? `/vacation/${request?.vacation?.id}`
			: '/vacation';
		const action = isEdit ? 'editadas' : 'cadastradas';
		const method = isEdit ? 'put' : 'post';
		setIsSubmiting(true);
		let invalid = false;
		submissionData.vacationPeriods.forEach((period: VacationPeriod) => {
			period.vacationCovers.forEach((vacationCover: CoverPerson) => {
				if (!vacationCover.employeeId && !vacationCover.coverageType) {
					invalid = true;
					toast.error('É preciso preencher o colaborador da cobertura', {
						theme: 'colored',
						toastId: 'coverageEmployee',
					});
				} else if (!vacationCover.startDate) {
					invalid = true;
					toast.error('Data de cobertura não pode estar vazia', {
						theme: 'colored',
						toastId: 'coverageDate',
					});
				}
			});
		});

		modalData.vacationPeriods.forEach(
			(period: VacationPeriod, periodIndex: number) => {
				period.vacationCovers?.forEach(
					(cover: CoverWithTransport, coverIndex: number) => {
						if (cover.needTransport && cover.transportData) {
							const { transportDays, passengerInfo } = cover.transportData;

							transportDays?.forEach((day: TransportDay, dayIndex: number) => {
								if (!day.transportType) {
									invalid = true;
									toast.error(
										`Tipo de transporte é obrigatório para o dia ${dayIndex + 1} da cobertura ${coverIndex + 1}`,
										{
											theme: 'colored',
											toastId: `transport-type-${periodIndex}-${coverIndex}-${dayIndex}`,
										},
									);
								}
								if (day.transportType === 'None') return;

								if (!day.entryTime || day.entryTime.trim() === '') {
									invalid = true;
									toast.error(
										`Horário de entrada é obrigatório para o dia ${dayIndex + 1} da cobertura ${coverIndex + 1}`,
										{
											theme: 'colored',
											toastId: `transport-entry-${periodIndex}-${coverIndex}-${dayIndex}`,
										},
									);
								}
								if (!day.exitTime || day.exitTime.trim() === '') {
									invalid = true;
									toast.error(
										`Horário de saída é obrigatório para o dia ${dayIndex + 1} da cobertura ${coverIndex + 1}`,
										{
											theme: 'colored',
											toastId: `transport-exit-${periodIndex}-${coverIndex}-${dayIndex}`,
										},
									);
								}
								if (!day.location) {
									invalid = true;
									toast.error(
										`Local é obrigatório para o dia ${dayIndex + 1} da cobertura ${coverIndex + 1}`,
										{
											theme: 'colored',
											toastId: `transport-location-${periodIndex}-${coverIndex}-${dayIndex}`,
										},
									);
								}
							});

							if (!passengerInfo?.phone || passengerInfo.phone.trim() === '') {
								invalid = true;
								toast.error(
									`Telefone do passageiro é obrigatório para a cobertura ${coverIndex + 1}`,
									{
										theme: 'colored',
										toastId: `transport-phone-${periodIndex}-${coverIndex}`,
									},
								);
							}
						}
					},
				);
			},
		);

		if (invalid) {
			setIsSubmiting(false);
			return;
		}

		await api[method](endpoint, submissionData)
			.then(
				async () => {
					toast.success(`Férias ${action} com sucesso`, {
						theme: 'colored',
						toastId: 'success',
					});
					queryClient.invalidateQueries({ queryKey: ['team-structure'] });
					queryClient.invalidateQueries({
						queryKey: ['vacationData', employeeId],
					});
					handleClose();
				},
				(error) => {
					const errorMessage =
						error?.response?.status < 500
							? error.response?.data?.message
							: 'Não conseguimos processar sua solicitação. Tente novamente mais tarde!';
					toast.error(errorMessage, {
						theme: 'colored',
						toastId: 'error',
					});
				},
			)
			.finally(() => {
				setIsSubmiting(false);
			});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset do form ao alternar modalIsOpen
	useEffect(() => {
		methods.reset();
	}, [modalIsOpen, methods]);
	const getReferenceDate = (date: string | undefined) => {
		const today = new Date();
		if (date) {
			return new Date(Math.max(civilDate(date).valueOf(), today.valueOf()));
		}
		return today;
	};

	const updateSalaryAdvanceCheckboxes = (index: number) => {
		if (methods.getValues(`vacationPeriods.${index}.salaryAdvance`) === true) {
			vacationPeriodsWatch.forEach((_period, idx) => {
				if (idx !== index) {
					methods.setValue(`vacationPeriods.${idx}.salaryAdvance`, false);
				}
			});
		}
	};

	const shouldDisableSalaryAdvanceCheckbox = (index: number) => {
		const isRhOrAdmin = userIsRhOrAdmin();
		if (isRhOrAdmin) {
			return false;
		}
		const vacationPeriod = vacationPeriodsWatch[index];
		if (vacationPeriod?.startDate) {
			return minimumDate > vacationPeriod.startDate;
		}
	};

	return (
		<Transition appear show={true}>
			<Dialog className="relative z-50" onClose={handleClose}>
				<div className="fixed inset-0 flex w-screen items-center justify-center bg-black/30 p-4">
					<Transition.Child
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<Dialog.Panel className="custom-scrollbar max-h-[85vh] w-200 flex-col justify-between overflow-auto overflow-y-scroll rounded-lg bg-white">
							<ModalConfirmation
								isOpen={!!employeeDeallocation}
								title="Desalocação de colaborador"
								description={employeeDeallocation?.map(
									(deallocation, index) => {
										if (deallocation.teamName === deallocation.coverageTeamName)
											return (
												<p
													className="py-2"
													key={`same-${deallocation.coverageEmployeeName}-${deallocation.employeeName}-${index}`}
												>
													O/A ferista {deallocation.coverageEmployeeName} irá
													cobrir o/a colaborador(a) {deallocation.employeeName}{' '}
													na turma {deallocation.coverageTeamName} no(s) dia(s){' '}
													{deallocation.days
														.map((day) => moment(day).format('DD/MM/YYYY'))
														.join(', ')}
													.
												</p>
											);
										else
											return (
												<p
													className="py-2"
													key={`diff-${deallocation.coverageEmployeeName}-${deallocation.teamName}-${index}`}
												>
													O/A colaborador(a) {deallocation.coverageEmployeeName}{' '}
													será desalocado/a da turma{' '}
													{deallocation.coverageTeamName} no(s) dia(s){' '}
													{deallocation.days
														.map((day) => moment(day).format('DD/MM/YYYY'))
														.join(', ')}{' '}
													para cobrir {deallocation.employeeName} na turma{' '}
													{deallocation.teamName}.
												</p>
											);
									},
								)}
								confirmAction={onSubmit}
								discartAction={() => setEmployeeDeallocation(null)}
								onClose={() => setEmployeeDeallocation(null)}
							/>
							<div className="w-full p-5">
								<div className="flex h-auto items-center justify-between">
									<div className="flex justify-between">
										<SubtitleText
											className="text-[20px]"
											subtitle={`Planejamento de Férias ${
												request
													? request?.isDoctor
														? '- Médicos'
														: request?.isADM
															? '- Regime Administrativo'
															: '- Regime de Turno'
													: ''
											}`}
										/>
									</div>
									<CloseButton onClick={handleClose} className="-mt-4" />
								</div>
								<SimpleSelect
									width="45rem"
									title="Período aquisitivo"
									values={qualifyingPeriods ?? []}
									value={selectedQualifyingPeriod}
									setState={(value) =>
										handleSelectQualifyingPeriod(value as number)
									}
								/>
							</div>
							{request && (
								<FormProvider {...methods}>
									<form
										className="space-between flex h-[90%] flex-col pt-2 "
										onSubmit={methods.handleSubmit(verifyCoverage)}
									>
										<div className="p-5 pt-0">
											<div className="mb-2 flex w-180 justify-between">
												<ControlledSimpleSelect
													name="employeeId"
													width="23rem"
													title={'Nome'}
													control={methods.control}
													values={[
														{
															value: request?.id,
															label: request?.name,
														},
													]}
												/>
												<ControlledSimpleSelect
													width="9.5rem"
													title={'Total de férias'}
													control={methods.control}
													name="totalVacationDays"
													values={getTotalDays()}
												/>
												<ControlledSimpleSelect
													defaultValue={request?.vacation?.vacationTypeId ?? ''}
													name="vacationScale"
													width="9.5rem"
													control={methods.control}
													title={'Formato de férias'}
													values={getScales(totalDaysWatch)}
												/>
											</div>
											{!(request.isADM || request.isDoctor) && (
												<ControlledCheckbox
													title={'Férias fora da política'}
													name="outOfPolicy"
													disabled={!canSaveOutOfPolicy}
													control={methods.control}
												/>
											)}
											{periods && (
												<div>
													{periods.name
														.split('x')
														.map((el: string, index: number) => {
															return (
																<ExpandableSubtitle
																	key={`period-${el}-${index}`}
																	subtitle={`Período de ${el} dias`}
																>
																	<div className="mb-2 flex w-180 justify-between">
																		<ControlledCalendar
																			referenceDate={getReferenceDate(
																				allowedStartDates[parseInt(el)]?.at(0),
																			)}
																			width="22rem"
																			title={'Data de início'}
																			control={methods.control}
																			name={`vacationPeriods.${index}.startDate`}
																			onChange={async (e) => {
																				if (e) {
																					const start = civilDate(e);
																					const days = parseInt(el, 10);
																					const endDateNumber =
																						addInclusiveCalendarDays(
																							start,
																							days,
																						);

																					methods.setValue(
																						`vacationPeriods.${index}.endDate`,
																						endDateNumber,
																					);
																					const startDate = formatDate(start);
																					const endDate = formatDate(
																						endDateNumber,
																					);
																					const data = await fetchVacationCover(
																						startDate,
																						endDate,
																					);
																					methods.setValue(
																						`vacationPeriods.${index}.vacationCoversWorkDays`,
																						data,
																					);
																				} else {
																					methods.setValue(
																						`vacationPeriods.${index}.endDate`,
																						new Date(''),
																					);
																					methods.setValue(
																						`vacationPeriods.${index}.vacationCoversWorkDays`,
																						[],
																					);
																				}
																			}}
																			setCalendar={setCalendar}
																			shouldDisableDate={(day: Date) =>
																				!verifyStartDateAllowed(
																					[
																						{
																							startDate:
																								removeTimeZone(
																									day,
																								).toDateString(),
																							totalDays: parseInt(el),
																						},
																					],
																					allowedStartDates,
																				)
																			}
																		/>
																		<ControlledCalendar
																			width="22rem"
																			disabled
																			title={'Data final'}
																			name={`vacationPeriods.${index}.endDate`}
																			control={methods.control}
																			disabledStyle="gray-filled"
																		/>
																	</div>
																	<div className="mb-5">
																		<ControlledCheckbox
																			title={'Adiantamento do 13º'}
																			name={`vacationPeriods.${index}.salaryAdvance`}
																			control={methods.control}
																			onChange={() =>
																				updateSalaryAdvanceCheckboxes(index)
																			}
																			disabled={shouldDisableSalaryAdvanceCheckbox(
																				index,
																			)}
																		/>
																	</div>
																	{!(request.isADM || request.isDoctor) && (
																		<VacationCoverPlan
																			index={index}
																			employees={request?.coverageEmployees}
																			vacationCovers={
																				vacationPeriodsWatch?.[index]
																					?.vacationCoversWorkDays
																			}
																			structures={structures}
																		/>
																	)}
																</ExpandableSubtitle>
															);
														})}
												</div>
											)}
										</div>
										<div className={calendar ? 'h-40' : ''}></div>
										<div className="flex justify-between p-5">
											<Button
												type="button"
												variant="outline"
												className="min-w-40 "
												onClick={handleClose}
											>
												Cancelar
											</Button>
											<Tooltip
												title={
													blockedByOutOfPolicy
														? PermissionMessages.OutOfPolicyNoPermission
														: PermissionMessages.NoPermission
												}
												disableHoverListener={
													!blockedByOutOfPolicy &&
													userCanSave(
														request?.managementId ?? null,
														actualUser,
													)
												}
											>
												<span>
													<Button
														type="submit"
														isLoading={isSubmiting}
														className="min-w-40 "
														disabled={
															blockedByOutOfPolicy ||
															!userCanSave(
																request?.managementId ?? null,
																actualUser,
															)
														}
													>
														Confirmar
													</Button>
												</span>
											</Tooltip>
										</div>
									</form>
								</FormProvider>
							)}
							{isFetchingVacation && (
								<div className="flex h-40 w-full items-center">
									<Spinner size={50} />
								</div>
							)}
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	);
}
